import { Database, Share2, WandSparkles, FileSpreadsheet } from "lucide-react";

/* ---- favicon helper (Google s2 service; always resolves to an icon) ------- */
function Favicon({ domain, label, className }: { domain: string; label: string; className?: string }) {
  return (
    <img
      src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
      alt={label}
      width={16}
      height={16}
      loading="lazy"
      className={className}
    />
  );
}

/* ======================================================================== *
 *  Infinite workbook data-plane
 * ======================================================================== */

type Status = "synced" | "syncing" | "mapping" | "draft";
type Product = {
  sku: string;
  name: string;
  ai?: boolean;
  brand: string;
  description: string;
  shopifyCat: string;
  gender: string;
  ageGroup: string;
  color: string;
  inventory: number;
  image: string;
  price: string;
  status: Status;
  statusLabel: string;
};

const PRODUCTS: Product[] = [
  { sku: "PRD-0492", name: "Merino Wool Scarf", ai: true, brand: "Loomera", description: "Hand-loomed merino wool, 180x30cm, twill weave", shopifyCat: "Apparel & Accessories > Clothing Accessories > Scarves", gender: "Women", ageGroup: "Adult", color: "Charcoal", inventory: 142, image: "dropbox.com/s/8f2k9/scarf-01.jpg", price: "48", status: "synced", statusLabel: "Synced" },
  { sku: "PRD-0518", name: "Linen Kurta Set", brand: "Terra & Thread", description: "Pre-washed linen kurta with tapered pants", shopifyCat: "Apparel & Accessories > Clothing > Outfit Sets", gender: "Men", ageGroup: "Adult", color: "Sand", inventory: 64, image: "dropbox.com/s/k29d3/kurta-02.jpg", price: "72", status: "syncing", statusLabel: "Syncing" },
  { sku: "PRD-0533", name: "Hand-Block Dupatta", ai: true, brand: "Anvi", description: "Cotton dupatta, indigo block print, 2.4m", shopifyCat: "Apparel & Accessories > Clothing Accessories > Shawls", gender: "Women", ageGroup: "Adult", color: "Indigo", inventory: 8, image: "dropbox.com/s/p0w1a/dupatta.jpg", price: "39", status: "mapping", statusLabel: "Mapping" },
  { sku: "PRD-0547", name: "Cotton Canvas Tote", brand: "Saorsa", description: "Heavyweight 12oz canvas tote, reinforced straps", shopifyCat: "Luggage & Bags > Shopping Totes", gender: "Unisex", ageGroup: "All ages", color: "Natural", inventory: 318, image: "dropbox.com/s/3a7c2/tote.jpg", price: "24", status: "synced", statusLabel: "Synced" },
  { sku: "PRD-0561", name: "Printed Silk Stole", brand: "Loomera", description: "Mulberry silk stole, digital floral print", shopifyCat: "Apparel & Accessories > Clothing Accessories > Scarves", gender: "Women", ageGroup: "Adult", color: "Rose", inventory: 47, image: "dropbox.com/s/9b2e7/stole.jpg", price: "33", status: "draft", statusLabel: "Draft" },
  { sku: "PRD-0574", name: "Jute Table Runner", ai: true, brand: "Terra & Thread", description: "Natural jute runner, tassel ends, 180cm", shopifyCat: "Home & Garden > Linens > Table Linens", gender: "Unisex", ageGroup: "All ages", color: "Wheat", inventory: 96, image: "dropbox.com/s/m4k8p/runner.jpg", price: "19", status: "synced", statusLabel: "Synced" },
  { sku: "PRD-0588", name: "Ceramic Mug Set", brand: "Kiln & Co", description: "Set of 4 stoneware mugs, reactive glaze, 300ml", shopifyCat: "Home & Garden > Kitchen & Dining > Drinkware", gender: "Unisex", ageGroup: "Adult", color: "Slate", inventory: 23, image: "dropbox.com/s/7t1q4/mugs.jpg", price: "28", status: "syncing", statusLabel: "Syncing" },
  { sku: "PRD-0601", name: "Brass Pendant Lamp", brand: "Anvi", description: "Hand-spun brass pendant, E27, 1.5m cord", shopifyCat: "Home & Garden > Lighting > Lamps", gender: "Unisex", ageGroup: "All ages", color: "Brass", inventory: 11, image: "dropbox.com/s/2v9x8/lamp.jpg", price: "96", status: "mapping", statusLabel: "Mapping" },
  { sku: "PRD-0615", name: "Bamboo Cutlery Kit", ai: true, brand: "Saorsa", description: "Travel cutlery set in cotton pouch, 5 pieces", shopifyCat: "Home & Garden > Kitchen & Dining > Flatware", gender: "Unisex", ageGroup: "All ages", color: "Honey", inventory: 205, image: "dropbox.com/s/6h3w1/cutlery.jpg", price: "14", status: "synced", statusLabel: "Synced" },
];

const PCOLS: { name: string; type: string; tclass: string }[] = [
  { name: "#", type: "row", tclass: "ttag--id" },
  { name: "SKU", type: "id", tclass: "ttag--id" },
  { name: "Product Name", type: "text", tclass: "ttag--text" },
  { name: "Brand", type: "text", tclass: "ttag--text" },
  { name: "Description", type: "text", tclass: "ttag--text" },
  { name: "Shopify Category", type: "taxonomy", tclass: "ttag--enum" },
  { name: "Gender", type: "enum", tclass: "ttag--enum" },
  { name: "Age Group", type: "enum", tclass: "ttag--enum" },
  { name: "Color", type: "text", tclass: "ttag--text" },
  { name: "Inventory", type: "number", tclass: "ttag--number" },
  { name: "Image", type: "url", tclass: "ttag--image" },
  { name: "Price", type: "number", tclass: "ttag--number" },
  { name: "Status", type: "enum", tclass: "ttag--enum" },
];
const NCOLS = PCOLS.length;

/* Muted swatches for the Color cells (the plane's low opacity fades them more). */
const COLOR_MAP: Record<string, string> = {
  Charcoal: "#454a52",
  Sand: "#d7c29a",
  Indigo: "#5566b8",
  Natural: "#e0d6c2",
  Rose: "#d98ba0",
  Wheat: "#e2c98c",
  Slate: "#6c7886",
  Brass: "#bd9a52",
  Honey: "#d8a64a",
};

const ROWS_PER_BLOCK = 13;
const ROW_BASE = 48217;
const SKL_W = ["", "62%", "74%", "58%", "86%", "82%", "52%", "58%", "50%", "40%", "80%", "38%", "60%"];

/* Deterministic so server render is stable (no Math.random). */
function isSkeleton(globalRow: number, col: number): boolean {
  if (col === 0) return false; // row-number gutter is always present
  if (globalRow % 11 === 5) return true; // whole row still loading
  if (globalRow % 11 === 8 && col >= 7) return true; // tail of row processing
  return (globalRow * 5 + col * 7) % 10 === 0;
}

function PlaneValue({ col, p, rownum }: { col: number; p: Product; rownum: number }) {
  switch (col) {
    case 0:
      return <>{rownum.toLocaleString()}</>;
    case 1:
      return <span className="v-sku">{p.sku}</span>;
    case 2:
      return (
        <>
          {p.ai && <span className="ai-spark">✦ AI</span>}
          <span className="v-ellip v-name">{p.name}</span>
        </>
      );
    case 3:
      return <span className="v-ellip">{p.brand}</span>;
    case 4:
      return <span className="v-ellip v-muted">{p.description}</span>;
    case 5:
      return <span className="v-ellip v-muted">{p.shopifyCat}</span>;
    case 6:
      return <span className="v-muted">{p.gender}</span>;
    case 7:
      return <span className="v-muted">{p.ageGroup}</span>;
    case 8:
      return (
        <>
          <span className="color-dot" style={{ background: COLOR_MAP[p.color] ?? "#b9bdc7" }} />
          <span className="v-muted">{p.color}</span>
        </>
      );
    case 9:
      return <span className="v-num">{p.inventory}</span>;
    case 10:
      return <span className="v-ellip v-link">{p.image}</span>;
    case 11:
      return <span className="v-price">{p.price}</span>;
    case 12:
      return <span className={`pill pill--${p.status}`}>{p.statusLabel}</span>;
    default:
      return null;
  }
}

function PlaneBlock({ tag }: { tag: string }) {
  return (
    <>
      {/* header (columns duplicated so the horizontal pan loops seamlessly) */}
      <div className="phead" key={`${tag}-head`}>
        {Array.from({ length: NCOLS * 3 }).map((_, ci) => {
          const c = PCOLS[ci % NCOLS];
          return (
            <div className="pcell" key={ci}>
              <span className="ghead__name">{c.name}</span>
              <span className={`ttag ${c.tclass}`}>{c.type}</span>
            </div>
          );
        })}
      </div>

      {/* rows */}
      {Array.from({ length: ROWS_PER_BLOCK }).map((_, i) => {
        const p = PRODUCTS[i % PRODUCTS.length];
        const rownum = ROW_BASE + i;
        return (
          <div className="prow" key={`${tag}-${i}`}>
            {Array.from({ length: NCOLS * 3 }).map((_, ci) => {
              const col = ci % NCOLS;
              const skl = isSkeleton(i, col);
              return (
                <div className={`pcell${col === 0 ? " pcell--num" : ""}`} key={ci}>
                  {skl ? (
                    <span className="skl" style={{ width: SKL_W[col] }} />
                  ) : (
                    <PlaneValue col={col} p={p} rownum={rownum} />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

const DESTINATIONS = [{ n: "Shopify", d: "shopify.com" }];

/* Schema sources for the "Set up your schema" card. csv has no brand, so it
   falls back to a file icon. */
const SCHEMA_SOURCES: { n: string; d?: string; csv?: boolean }[] = [
  { n: "Excel", d: "microsoft.com" },
  { n: "CSV", csv: true },
];

/* AI providers for the "Rules & AI" card. */
const AI_TOOLS = [
  { n: "Claude", d: "claude.ai" },
  { n: "ChatGPT", d: "chatgpt.com" },
];

/* Full-page animated workbook background (rendered behind the whole page). */
export function WorkbookPlane() {
  return (
    <>
      <div className="plane-stage" aria-hidden>
        <div className="plane">
          {/* panx = horizontal pan to the right; track = upward float */}
          <div className="plane-panx">
            <div className="plane-track">
              <PlaneBlock tag="a" />
              <PlaneBlock tag="b" />
            </div>
          </div>
        </div>
      </div>
      <div className="plane-scrim" aria-hidden />
    </>
  );
}

export function SchemaShowcase() {
  return (
    <>
      {/* bento info grid overlaid at the bottom */}
      <div className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-12 pt-10 xl:px-12">
        <div className="bento">
          {/* 1 — Set up your schema */}
          <div className="bento-card col-span-6 sm:col-span-2">
            <div className="mb-2.5 flex items-center gap-2.5">
              <Database size={22} strokeWidth={2.2} className="shrink-0 text-[var(--t-text)]" />
              <h3 className="text-[18px] font-semibold text-[var(--p-ink)]">Bring any vendor sheet</h3>
            </div>
            <p className="mb-3 text-[13.5px] leading-relaxed text-[var(--p-muted)]">
              Upload the Excel or CSV your supplier sent, messy headers and all. Save the mapping
              once as a template and reuse it for every new sheet.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SCHEMA_SOURCES.map((s) => (
                <span className="mp-chip" key={s.n} title={s.n}>
                  {s.csv ? (
                    <FileSpreadsheet size={16} strokeWidth={2} className="text-[var(--p-muted)]" />
                  ) : (
                    <Favicon domain={s.d!} label={s.n} className="mp-fav" />
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* 2 — Rules & AI */}
          <div className="bento-card col-span-6 sm:col-span-2">
            <div className="mb-2.5 flex items-center gap-2.5">
              <WandSparkles size={22} strokeWidth={2.2} className="shrink-0 text-[var(--t-ai)]" />
              <h3 className="text-[18px] font-semibold text-[var(--p-ink)]">Rules &amp; AI</h3>
            </div>
            <p className="mb-3 text-[13.5px] leading-relaxed text-[var(--p-muted)]">
              Add rules, conditions and AI to any field. Auto-fill and tidy your data, or let AI
              write titles, descriptions and tags for you.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {AI_TOOLS.map((t) => (
                <span className="mp-chip" key={t.n} title={t.n}>
                  <Favicon domain={t.d} label={t.n} className="mp-fav" />
                </span>
              ))}
            </div>
          </div>

          {/* 3 — List everywhere */}
          <div className="bento-card col-span-6 sm:col-span-2">
            <div className="mb-2.5 flex items-center gap-2.5">
              <Share2 size={22} strokeWidth={2.2} className="shrink-0 text-[var(--t-number)]" />
              <h3 className="text-[18px] font-semibold text-[var(--p-ink)]">Import-ready for Shopify</h3>
            </div>
            <p className="mb-3 text-[13.5px] leading-relaxed text-[var(--p-muted)]">
              Export a Shopify product CSV with variants, images, taxonomy and metafields, checked
              against Shopify&apos;s import rules before you upload.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DESTINATIONS.map((m) => (
                <span className="mp-chip" key={m.n} title={m.n}>
                  <Favicon domain={m.d} label={m.n} className="mp-fav" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
